import { Button, ButtonType } from '@ardoq/button';
import { Checkbox } from '@ardoq/forms';
import { FlexBox, Stack } from '@ardoq/layout';
import {
  AnimatedModalDialog,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalLayout,
  ModalSize,
  ModalWrapper,
} from '@ardoq/modal';
import { Text } from '@ardoq/typography';

export type DuplicateCopyProps = {
  id: string;
  label: string;
  isChecked: boolean;
  onToggle: VoidFunction;
};

export type DuplicateGroupProps = {
  id: string;
  title: string;
  subtitle: string;
  keptText: string;
  copies: DuplicateCopyProps[];
};

export type DuplicatesModalProps = {
  isOpen: boolean;
  headerText: string;
  summaryText: string;
  hiddenText: string | null;
  groups: DuplicateGroupProps[];
  footer: DuplicatesModalFooterProps;
  onCancel: VoidFunction;
};

export type DuplicatesModalFooterProps =
  | {
      kind: 'delete';
      cancelButtonText: string;
      deleteButtonText: string;
      isDeleteDisabled: boolean;
      onDelete: VoidFunction;
      onCancel: VoidFunction;
    }
  | {
      kind: 'close';
      closeButtonText: string;
      onClose: VoidFunction;
    };

const DuplicatesModalFooter = (props: DuplicatesModalFooterProps) => {
  if (props.kind === 'close') {
    return (
      <ModalFooter>
        <Button onClick={props.onClose} buttonType={ButtonType.SECONDARY}>
          {props.closeButtonText}
        </Button>
      </ModalFooter>
    );
  }
  return (
    <ModalFooter>
      <Button onClick={props.onCancel} buttonType={ButtonType.SECONDARY}>
        {props.cancelButtonText}
      </Button>
      <Button
        onClick={props.onDelete}
        buttonType={ButtonType.DANGER}
        isDisabled={props.isDeleteDisabled}
      >
        {props.deleteButtonText}
      </Button>
    </ModalFooter>
  );
};

const DuplicateCopy = (props: DuplicateCopyProps) => (
  <Checkbox isChecked={props.isChecked} onChange={props.onToggle}>
    {props.label}
  </Checkbox>
);

const DuplicateGroup = (props: DuplicateGroupProps) => (
  <Stack gap="xsmall" role="group">
    <FlexBox align="baseline" gap="small">
      <Text variant="text1Bold">{props.title}</Text>
      <Text variant="text2" color="textSubtle">
        {props.subtitle}
      </Text>
    </FlexBox>
    <Text variant="text2" color="textSubtle">
      {props.keptText}
    </Text>
    {props.copies.map(copy => (
      <DuplicateCopy key={copy.id} {...copy} />
    ))}
  </Stack>
);

export const DuplicatesModal = (props: DuplicatesModalProps) => (
  <AnimatedModalDialog
    isOpen={props.isOpen}
    onClickOutside={props.onCancel}
    onEscPress={props.onCancel}
  >
    <ModalContainer modalSize={ModalSize.M}>
      <ModalHeader onCloseButtonClick={props.onCancel}>
        {props.headerText}
      </ModalHeader>
      <ModalWrapper>
        <ModalLayout>
          <Stack gap="large">
            <Stack gap="xsmall">
              <Text>{props.summaryText}</Text>
              {props.hiddenText && (
                <Text variant="text2" color="textSubtle">
                  {props.hiddenText}
                </Text>
              )}
            </Stack>
            {props.groups.map(group => (
              <DuplicateGroup key={group.id} {...group} />
            ))}
          </Stack>
        </ModalLayout>
      </ModalWrapper>
      <DuplicatesModalFooter {...props.footer} />
    </ModalContainer>
  </AnimatedModalDialog>
);
